import { useQueryClient } from "@tanstack/react-query";
import { useGetList, useGetOne, useDataProvider, useNotify } from "ra-core";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { RelativeDate } from "../misc/RelativeDate";
import {
  DRAIN_JOB,
  SETTINGS_FIELDS,
  cancelOutbound,
  cancelOutcome,
  cancelViaSupabase,
  clearTripPatch,
  describeWait,
  drainNoteFor,
  dueNow,
  emptyQueueNote,
  holdSentence,
  invalidateOutbound,
  pausePatch,
  releaseText,
  resumePatch,
  validatePauseReason,
  validateSettings,
  wouldTripAgain,
  type CancelRpc,
  type DrainRun,
  type OutboundPolicy,
  type OutboundQueueRow,
  type SettingsDraft,
  type WaitTone,
} from "./outboundRules";

/**
 * Outbound email — the queue, and every control over it.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * Resend has no useful pause and no useful cancel: once a message is accepted
 * it is gone. So every control has to act BEFORE the API call, which is what
 * migration 067 built — a hold window, a kill switch, a rate brake, and a
 * queue you can see into. All of it was true in the database and invisible in
 * the product, which is the same failure the lifecycle had before
 * LifecyclePanel: a switch nobody can reach is a column, not a control. ops#8
 * asks for five things and four of them need a screen. This is that screen.
 *
 * WHY IT IS UNDER /contacts
 * -------------------------
 * These are messages to PEOPLE. /marketing is door-to-door mailing runs, and
 * Header.tsx picks the active top-level tab by matching the path, so a queue
 * living there would light **Pipeline** and sit under the wrong sub-nav. Same
 * reasoning paths.ts records for the follow-up queue.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHAT THIS FILE IS FORBIDDEN TO DO
 * ══════════════════════════════════════════════════════════════════════════
 * It does not decide why a message is waiting. `outbound_queue.wait_state` is
 * computed in SQL and 067 created it precisely because "held" and "due" are
 * different facts a merged screen cannot explain. A second opinion computed
 * here from `send_after` and the browser clock is how this page and the
 * drainer come to disagree about the same row — and the disagreement would
 * look like a countdown, which is the most believable kind of wrong. Every
 * derivation lives in ./outboundRules.ts and formats what the view decided.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * THE ORDER OF THIS PAGE IS AN ARGUMENT ABOUT URGENCY
 * ---------------------------------------------------
 * The trip is first, because a tripped brake means NOTHING is going out and
 * only a person can change that. Then the pause, then the queue, then the
 * settings — least reversible first, most reversible last. A trip rendered
 * below a settings form would be a stopped system reported as a footnote.
 */

/**
 * One page, large but finite.
 *
 * The whole point of the brake is that this list should never be long: more
 * than ten due at once is treated as a malfunction. 200 is far past any
 * legitimate state, and the list says so out loud rather than letting a
 * truncated view pass as the whole queue — which on this screen would mean
 * confirming "cancel all" against a count that is not the real one.
 */
const PAGE_SIZE = 200;

const TONE_TEXT: Record<WaitTone, string> = {
  normal: "text-muted-foreground",
  attention: "text-foreground",
  alarm: "text-destructive",
};

const TONE_BOX: Record<WaitTone, string> = {
  normal: "border",
  attention: "border-2",
  alarm: "border-2 border-destructive",
};

export const OutboundQueue = ({
  /**
   * cancel_outbound is a security-definer RPC, so it goes through the
   * Supabase client rather than the data provider. Injected for the reason
   * sendBatches() takes its `call`: the control being tested is the one that
   * destroys queued email, and it should be provable without a credential or
   * a network.
   */
  cancel = cancelViaSupabase,
}: { cancel?: CancelRpc } = {}) => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const notify = useNotify();

  const {
    data: policy,
    error: policyError,
    isPending: policyPending,
  } = useGetOne<OutboundPolicy & { id: number }>("outbound_policy", { id: 1 });

  const {
    data: rows,
    total,
    error: queueError,
    isPending: queuePending,
  } = useGetList<OutboundQueueRow>("outbound_queue", {
    pagination: { page: 1, perPage: PAGE_SIZE },
    // The queue's own order: what leaves next is what has waited longest.
    sort: { field: "send_after", order: "ASC" },
    filter: {},
  });

  /*
    THE SENDER'S OWN RECORD, WHICH IS THE ONLY HONEST SOURCE FOR "DOES
    ANYTHING ACTUALLY GO OUT?"

    What stood here was a hardcoded paragraph asserting that drain-outbound
    was not deployed. It was deployed on 2026-09-02 and has run 19 times
    since, so the screen asserted that email could not be sent while it was
    being sent -- for two days, with nothing anywhere able to fail. atomic#10.

    THE `job` FILTER IS NOT OPTIONAL. `pipeline_runs` is shared by five jobs
    and the nightly tick writes to it too, so an unfiltered read would answer
    this question with somebody else's run and take the warning down because
    nurture-tick was healthy. describeDrain() filters again on the rows it is
    handed, so this query being wrong degrades to "no drain rows" rather than
    to a confident lie -- but five rows of another job would then read as
    "never run", which is why both halves exist.
  */
  const {
    data: drainRows,
    error: drainError,
    isPending: drainPending,
  } = useGetList<DrainRun & { id: number }>("pipeline_runs", {
    filter: { job: DRAIN_JOB },
    /*
      TWENTY-FIVE, NOT FIVE, BECAUSE THESE ROWS ARE NOW A MEASUREMENT.
      describeDrain() only ever reads the newest one, so five was plenty for
      it. holdSentence() reads the INTERVALS between them to tell a person how
      long they really have to unsend, and five rows is four intervals -- half
      a day at the cadence measured in atomic#16, which one stall would skew
      badly. Twenty-five spans a few days of the sender's actual behavior and
      costs one page of very small rows.
    */
    pagination: { page: 1, perPage: 25 },
    sort: { field: "started_at", order: "DESC" },
  });

  /*
    Pending, failed and answered are three different things and only one of
    them is "the sender has never run" -- see drainNoteFor(), which is where
    that decision lives so it can be tested in Node rather than inferred from
    a ternary nothing can reach.
  */
  const drain = drainNoteFor({
    isPending: drainPending,
    error: drainError,
    rows: drainRows,
  });

  const [busy, setBusy] = useState(false);

  const refresh = () => invalidateOutbound(queryClient);

  const writePolicy = async (patch: object, done: string) => {
    if (!policy) return;
    setBusy(true);
    try {
      await dataProvider.update("outbound_policy", {
        id: 1,
        data: patch,
        previousData: policy,
      });
      await refresh();
      notify(done, { type: "success" });
    } catch (e) {
      notify((e as Error).message ?? "Could not write the outbound policy", {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Outbound email</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Everything waiting to go out, and every control over it. Resend has no
          pause and no cancel — once a message is accepted it is gone — so all
          of this acts before the API call, on{" "}
          <code className="text-xs">public.outbound_queue</code> and{" "}
          <code className="text-xs">public.outbound_policy</code>.
        </p>
        {/*
          DERIVED, SO IT CAN GO AWAY BY ITSELF. A healthy drain produces no
          note at all -- describeDrain() returns null -- and every sentence
          that does appear names a state a person has to act on: never ran,
          stopped, failing, or unreadable. The alarm border is spent only on
          those, for the reason describeWait() spends none on `held`.
        */}
        {drain?.note && (
          <p
            className={cn(
              "mt-2 max-w-3xl rounded-md px-3 py-2 text-sm",
              TONE_BOX[drain.tone],
              TONE_TEXT[drain.tone],
            )}
          >
            {drain.note}
          </p>
        )}
      </div>

      {/*
        THE POLICY IS THE PAGE. Every control writes to that one row, so a
        failed read is not a screen missing some numbers — it is a screen that
        cannot honestly offer a single button. Offering them anyway would mean
        a Pause that writes into a row we could not fetch, over state we do not
        know.
      */}
      {policyError ? (
        <p className="text-sm text-destructive">
          Could not read <code className="text-xs">outbound_policy</code>, so
          this is not a report that email is flowing normally — it is a report
          that we do not know whether it is flowing at all. No control is
          offered over a row we could not fetch.
        </p>
      ) : policyPending ? (
        <p className="text-sm text-muted-foreground">Loading the policy…</p>
      ) : (
        <>
          <TripBanner
            policy={policy}
            dueCount={dueNow(rows ?? []).length}
            busy={busy}
            onResume={() =>
              writePolicy(
                clearTripPatch(),
                "Brake cleared. The next drainer run will look at the queue again.",
              )
            }
          />
          <PauseControl
            policy={policy}
            busy={busy}
            onPause={(reason) =>
              writePolicy(pausePatch(reason), "Outbound email paused.")
            }
            onResume={() =>
              writePolicy(resumePatch(), "Outbound email resumed.")
            }
          />
        </>
      )}

      <QueueList
        rows={rows}
        total={total}
        pending={queuePending}
        error={queueError}
        policy={policyError ? null : (policy ?? null)}
        cancel={cancel}
        onDone={refresh}
      />

      {policy && !policyError ? (
        <Settings
          policy={policy}
          busy={busy}
          onSave={writePolicy}
          /*
            THE SAME ROWS THE DRAIN NOTE READS. holdSentence() derives the
            unsend window from what the sender has actually been doing, so it
            needs the evidence rather than a constant -- atomic#16. A failed
            read is passed as null, not as [], for the reason drainNoteFor()
            draws that distinction: an unreadable pipeline_runs is not a
            sender that has never run, and neither one may print a window.
          */
          drainRuns={drainError ? null : (drainRows ?? null)}
        />
      ) : null}
    </div>
  );
};

/* ── the trip ──────────────────────────────────────────────────────────── */

/**
 * The brake, when it has tripped — the loudest thing on the page.
 *
 * `tripped_reason` IS SHOWN VERBATIM. claim_outbound_batch() formats it with
 * the actual numbers in it — how many became due, and against which limit —
 * and those two numbers are the entire basis on which somebody decides
 * whether resuming is safe. A paraphrase ("too many messages") throws away
 * the only content the message had.
 */
const TripBanner = ({
  policy,
  dueCount,
  busy,
  onResume,
}: {
  policy: OutboundPolicy;
  dueCount: number;
  busy: boolean;
  onResume: () => void;
}) => {
  if (!policy.tripped_at) return null;
  const again = wouldTripAgain(policy, dueCount);

  return (
    <div className="rounded-md border-2 border-destructive p-4 space-y-3">
      <h2 className="text-base font-semibold text-destructive">
        The brake tripped. Nothing is being sent.
      </h2>
      {/* Verbatim. See the note above. */}
      <p className="text-sm">{policy.tripped_reason}</p>
      <p className="text-sm text-muted-foreground">
        A burst is treated as evidence of a bug rather than as a workload, so
        the drainer stopped instead of sending the first few. It stays stopped
        until a person clears it — deliberately, because whatever produced the
        burst is still there.
      </p>
      {again ? (
        /* Clearing a trip does not drain the queue; it only lets the next run
           apply the same brake to the same rows. Without this, Resume reads as
           having done nothing when it trips again five minutes later. */
        <p className="text-sm text-destructive">{again}</p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Tripped <RelativeDate date={policy.tripped_at} />.
      </p>
      <Button variant="outline" onClick={onResume} disabled={busy}>
        Clear the trip and resume
      </Button>
    </div>
  );
};

/* ── the kill switch ───────────────────────────────────────────────────── */

/**
 * Pause everything, or resume it. A pause states its reason.
 *
 * The reason is REQUIRED, and refused here rather than only by
 * `outbound_policy_pause_has_reason`, because a constraint error in a toast
 * teaches nothing and the reason exists for the next person: they find every
 * message stopped, and the only way to judge whether resuming is safe is to
 * know what stopped it. Third time this house rule appears — ending an
 * enrollment, cancelling a message, pausing the system.
 */
const PauseControl = ({
  policy,
  busy,
  onPause,
  onResume,
}: {
  policy: OutboundPolicy;
  busy: boolean;
  onPause: (reason: string) => void;
  onResume: () => void;
}) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (policy.paused)
    return (
      <div className="rounded-md border-2 p-4 space-y-3">
        <h2 className="text-base font-semibold">Outbound email is paused.</h2>
        <p className="text-sm">{policy.pause_reason}</p>
        <p className="text-sm text-muted-foreground">
          Messages still queue up while this is on — they wait in the list below
          rather than being lost — and none of them drains until it is off.
        </p>
        <Button variant="outline" onClick={onResume} disabled={busy}>
          Resume outbound email
        </Button>
      </div>
    );

  return (
    <div className="rounded-md border p-4 space-y-2">
      <h2 className="text-base font-semibold">Pause all outbound email</h2>
      <p className="text-sm text-muted-foreground">
        Stops the drainer taking anything at all. Nothing is lost: messages keep
        queueing and go out when you resume.
      </p>
      <Label htmlFor="pause-reason" className="text-sm">
        Why are you pausing outbound email?
      </Label>
      <Textarea
        id="pause-reason"
        value={reason}
        rows={2}
        className="max-w-xl"
        onChange={(e) => {
          setReason(e.target.value);
          setError(null);
        }}
      />
      {/* Inline, not only a toast: the reason belongs beside the field that
          caused it, and a toast is gone before anyone re-reads the box. */}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => {
          const check = validatePauseReason(reason);
          if (!check.ok) {
            setError(check.message);
            return;
          }
          setError(null);
          onPause(check.reason);
        }}
      >
        Pause outbound email
      </Button>
    </div>
  );
};

/* ── the queue ─────────────────────────────────────────────────────────── */

/** Which cancel confirmation is open. At most one, ever — see below. */
type Pending =
  | { kind: "all"; count: number }
  | { kind: "one"; row: OutboundQueueRow };

const QueueList = ({
  rows,
  total,
  pending,
  error,
  policy,
  cancel,
  onDone,
}: {
  rows: OutboundQueueRow[] | undefined;
  total: number | undefined;
  pending: boolean;
  error: unknown;
  policy: OutboundPolicy | null;
  cancel: CancelRpc;
  onDone: () => Promise<unknown>;
}) => {
  const notify = useNotify();
  /*
   * ONE pending confirmation at a time, held here rather than per row. Two
   * open at once — a row's and the cancel-all — would put two "Yes, cancel"
   * buttons on the page with different meanings, and the wrong one is
   * irreversible.
   */
  const [confirm, setConfirm] = useState<Pending | null>(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setConfirm(null);
    setReason("");
    setReasonError(null);
  };

  const run = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const ids = confirm.kind === "all" ? null : [confirm.row.id];
      const requested = confirm.kind === "all" ? confirm.count : 1;
      const cancelled = await cancelOutbound(cancel, ids, reason);
      await onDone();
      close();
      notify(cancelOutcome(requested, cancelled), {
        type: cancelled === 0 ? "warning" : "success",
      });
    } catch (e) {
      // The database's own refusals land here — "only staff may cancel
      // outbound email", and the blank-reason raise. Shown beside the field
      // rather than only as a toast, for the reason PauseControl gives.
      setReasonError((e as Error).message ?? "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  if (error)
    return (
      <div>
        <h2 className="text-base font-semibold">The queue</h2>
        <p className="mt-1 max-w-3xl text-sm text-destructive">
          <code className="text-xs">outbound_queue</code> could not be read, so
          this is not a report that nothing is waiting — it is a report that we
          cannot see what is. An empty list here would be indistinguishable from
          a denied read.
        </p>
      </div>
    );

  const list = rows ?? [];
  /*
   * cancel_outbound(NULL) covers everything queued, INCLUDING anything past
   * this page, so the confirmation counts `total` rather than the rows on
   * screen. Promising a smaller blast radius than the button has is the one
   * thing a confirmation must never do.
   */
  const allCount = total ?? list.length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">The queue</h2>
        {list.length > 0 && !confirm ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirm({ kind: "all", count: allCount })}
          >
            Cancel everything queued
          </Button>
        ) : null}
      </div>

      {confirm ? (
        <CancelConfirm
          confirm={confirm}
          reason={reason}
          error={reasonError}
          busy={busy}
          onReason={(v) => {
            setReason(v);
            setReasonError(null);
          }}
          onConfirm={run}
          onCancel={close}
        />
      ) : null}

      {pending ? (
        <p className="text-sm text-muted-foreground">Loading the queue…</p>
      ) : list.length === 0 ? (
        <EmptyQueue policy={policy} />
      ) : (
        <>
          <ul className="divide-y rounded-md border">
            {list.map((row) => (
              <QueueRow
                key={row.id}
                row={row}
                onCancel={
                  confirm ? undefined : () => setConfirm({ kind: "one", row })
                }
              />
            ))}
          </ul>
          {(total ?? 0) > list.length ? (
            <p className="text-xs text-muted-foreground">
              Showing the {list.length} soonest of {total}. The rest are still
              queued, and Cancel everything queued would take them too.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
};

/**
 * An empty queue, explained.
 *
 * "Nothing queued" and "nothing draining" are opposite facts that both render
 * an empty list, and the wording that separates them lives in
 * emptyQueueNote() where it can be tested in Node. See FollowUps.tsx for the
 * same discipline applied to three neglect queues.
 */
const EmptyQueue = ({ policy }: { policy: OutboundPolicy | null }) => {
  const note = emptyQueueNote(policy);
  return (
    <div className={cn("rounded-md p-4", TONE_BOX[note.tone])}>
      <p className={cn("text-sm font-medium", TONE_TEXT[note.tone])}>
        {note.headline}
      </p>
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
        {note.detail}
      </p>
    </div>
  );
};

const QueueRow = ({
  row,
  onCancel,
}: {
  row: OutboundQueueRow;
  onCancel?: () => void;
}) => {
  const wait = describeWait(row.wait_state);
  const release = releaseText(row);
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ");
  /*
   * A CLAIMED ROW GETS NO CANCEL BUTTON. cancel_outbound() refuses it — the
   * message may already be at Resend — and a button that always fails is worse
   * than none: it claims the message can still be stopped.
   */
  const cancellable = row.wait_state !== "sending";

  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2 text-sm">
      {row.contact_id ? (
        <Link
          to={`/contacts/${row.contact_id}/show`}
          className="font-medium underline-offset-2 hover:underline"
        >
          {name || `Contact ${row.contact_id}`}
        </Link>
      ) : (
        // A queued message with no contact should be impossible — the view
        // inner-joins contacts — but a blank recipient is the last thing this
        // screen should print about a message that is about to be sent.
        <span className="font-medium">no recipient on file</span>
      )}

      <span className="text-muted-foreground">
        {row.subject || "(no subject)"}
      </span>

      <Badge
        variant={wait.tone === "alarm" ? "destructive" : "outline"}
        className="text-[0.7rem] font-normal"
        title={wait.blurb}
      >
        {wait.label}
      </Badge>

      <span className={cn("text-xs", TONE_TEXT[wait.tone])}>
        {/* Null means releases_in did not parse — see intervalSeconds(). The
            absolute moment is the honest fallback; an invented countdown is
            not. */}
        {release ?? (
          <>
            eligible <RelativeDate date={row.send_after} />
          </>
        )}
      </span>

      {/* The states that need words get them; `held` and `due` do not, and
          spending the page's alarm on the healthy case is how `stopped` stops
          standing out. */}
      {wait.tone !== "normal" ? (
        <span className={cn("text-xs", TONE_TEXT[wait.tone])}>
          {wait.blurb}
        </span>
      ) : null}

      {row.last_error ? (
        <span className="text-xs text-destructive">{row.last_error}</span>
      ) : null}

      <span className="ml-auto flex items-center gap-3">
        {row.attempts > 0 ? (
          <span className="text-xs text-muted-foreground">
            {row.attempts} {row.attempts === 1 ? "attempt" : "attempts"}
          </span>
        ) : null}
        {cancellable && onCancel ? (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel this message
          </Button>
        ) : null}
      </span>
    </li>
  );
};

/**
 * The confirmation, for one message or for all of them.
 *
 * CANCEL-ALL CONFIRMS BEFORE IT FIRES, AND SAYS HOW MANY. It is irreversible
 * for every message it touches and sits one click from buttons that are not,
 * and "cancel all" over a queue of one and over a queue of forty are different
 * decisions — so the number is in the sentence rather than only in the list
 * above it.
 */
const CancelConfirm = ({
  confirm,
  reason,
  error,
  busy,
  onReason,
  onConfirm,
  onCancel,
}: {
  confirm: Pending;
  reason: string;
  error: string | null;
  busy: boolean;
  onReason: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const all = confirm.kind === "all";
  const count = all ? confirm.count : 1;
  const noun = count === 1 ? "message" : "messages";
  const who = all
    ? null
    : [confirm.row.first_name, confirm.row.last_name]
        .filter(Boolean)
        .join(" ") || `contact ${confirm.row.contact_id}`;

  return (
    <div className="rounded-md border-2 border-destructive p-4 space-y-2">
      <h3 className="text-sm font-semibold text-destructive">
        {all
          ? `Cancel all ${count} ${noun} still queued?`
          : `Cancel the message to ${who}?`}
      </h3>
      <p className="max-w-3xl text-sm text-muted-foreground">
        This cannot be undone for the {noun} it touches. Anything the drainer
        has already claimed is left alone — that message may already be at
        Resend, and recording it as cancelled would make the log deny a message
        that went out — so fewer than {count} may be affected.
      </p>
      <Label htmlFor="cancel-reason" className="text-sm">
        Why is this being cancelled?
      </Label>
      <Textarea
        id="cancel-reason"
        value={reason}
        rows={2}
        className="max-w-xl"
        onChange={(e) => onReason(e.target.value)}
      />
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex gap-2">
        <Button variant="destructive" onClick={onConfirm} disabled={busy}>
          Yes, cancel {all ? `all ${count} ${noun}` : "this message"}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Keep {all ? "them" : "it"} queued
        </Button>
      </div>
    </div>
  );
};

/* ── the settings ──────────────────────────────────────────────────────── */

/**
 * The three numbers ops#8 asks to be adjustable, each with the line that says
 * what it does.
 *
 * A number with no sentence beside it is a number nobody dares change, and
 * these are exactly the ones somebody will need to change at 11pm: the hold
 * when a mistake has to be caught, the two limits when a legitimate batch
 * keeps tripping the brake.
 */
const Settings = ({
  policy,
  busy,
  onSave,
  drainRuns,
}: {
  policy: OutboundPolicy;
  busy: boolean;
  onSave: (patch: object, done: string) => Promise<void>;
  drainRuns: readonly DrainRun[] | null;
}) => {
  const [draft, setDraft] = useState<SettingsDraft>({
    hold_minutes: String(policy.hold_minutes),
    max_per_run: String(policy.max_per_run),
    max_per_hour: String(policy.max_per_hour),
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof SettingsDraft, string>>
  >({});

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">Settings</h2>

      {/* THE HOLD IS A FLOOR, NOT A SCHEDULE, and this is the sentence most
          likely to be got wrong -- it already was, for the whole of atomic#16.
          It is DERIVED from the sender's own runs now, so it corrects itself
          when the sender changes. See holdSentence(). */}
      <p className="max-w-3xl text-sm text-muted-foreground">
        {holdSentence(policy.hold_minutes, drainRuns)}
      </p>

      <div className="space-y-4">
        {SETTINGS_FIELDS.map((field) => (
          <div key={field.key} className="space-y-1">
            <Label htmlFor={field.key} className="text-sm">
              {field.label}
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id={field.key}
                inputMode="numeric"
                className="w-28"
                value={draft[field.key]}
                onChange={(e) => {
                  setDraft({ ...draft, [field.key]: e.target.value });
                  setErrors({ ...errors, [field.key]: undefined });
                }}
              />
              <span className="text-sm text-muted-foreground">
                {field.unit}
              </span>
            </div>
            <p className="max-w-3xl text-sm text-muted-foreground">
              {field.what}
            </p>
            {errors[field.key] ? (
              <p className="text-sm text-destructive">{errors[field.key]}</p>
            ) : null}
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        disabled={busy}
        onClick={async () => {
          const check = validateSettings(draft);
          if (!check.ok) {
            setErrors(check.errors);
            return;
          }
          setErrors({});
          await onSave(check.patch, "Outbound settings saved.");
        }}
      >
        Save settings
      </Button>

      <p className="text-xs text-muted-foreground">
        Last changed <RelativeDate date={policy.updated_at} />.
      </p>
    </div>
  );
};
